import type { AnalysisReportTranslation } from "./index";

export const english = {
  analysis_report: "Analysis report",
  analysis_report_unrecognized:
    "This report was written by a different version of the analysis pipeline than this page can read — re-run the analysis to regenerate it in the current format.",
  analysis_report_absent:
    "This analysis predates pipeline diagnostics — re-run the analysis to generate the full report.",
  failed_to_load_analysis_report: "Failed to load the analysis report",
  provenance: "Provenance",
  recording_id: "Recording id",
  run_id: "Run id",
  build: "Build",
  resumed_from_stage_of_run: "Resumed from stage {{stage}} of run",
  resumed_from_run_deleted: "deleted",
  inherited: "Inherited",
  inherited_hint:
    "Copied from the run this analysis resumed from, not produced here. The figure is real \u2014 it describes the artifact this analysis is built on \u2014 but this run neither spent nor measured it.",
  not_recorded: "not recorded",
  section_not_recorded: "Not recorded for this analysis.",
  vocabulary_usage: "Vocabulary usage",
  cleanup_stats: "Cleanup Stage",
  cleanup_pass: "Pass",
  cleanup_remaining_events: "Remaining events",
  cleanup_delta: "Δ",
  cleanup_delta_hint: "Events the pass removed or added.",
  cleanup_pass_raw_events: "raw events",
  cleanup_pass_raw_events_hint: "Every event the recording captured, before any cleanup.",
  cleanup_pass_drop_metadata: "drop metadata (screenshots, app/tab switches)",
  cleanup_pass_insert_meeting_video: "insert meeting / watching-video",
  cleanup_pass_insert_meeting_video_hint:
    "Gaps longer than {{idle}} become a meeting or media event when the window name matches (e.g. Google Meet / YouTube).",
  cleanup_pass_merge_same_type: "merge same-type events",
  cleanup_pass_merge_same_type_hint:
    "Consecutive events of the same kind in the same window collapse into one entry — five scrolls become one 'scrolling', a shortcut twice becomes 'cmd + s x2'.",
  cleanup_pass_drop_clicks_without_screenshot: "drop clicks w/o concurrent screenshot",
  cleanup_pass_drop_short_tab_visits: "drop short tab visits",
  cleanup_pass_drop_short_tab_visits_hint: "Tab that lasted for less than {{tab}}.",
  cleanup_pass_merge_typing: "merge typing sessions",
  cleanup_cleaned_events: "Cleaned events",
  cleanup_event_types: "Event types (cleaned)",
  annotation_stats: "Annotator stage",
  cost_and_tokens: "Cost & tokens",
  lift_stages: "Lift stages",
  stages: "Stages",
  objects_stats: "Objects",
  clipboard_links: "Clipboard (L1)",
  clipboard_pairs: "Copy → paste pairs",
  clipboard_pairs_per_hour: "Pairs / hour",
  canonicalization: "Name canonicalization (L2)",
  objects_in: "Distinct names in",
  objects_out: "Canonical names out",
  distinct_objects: "Distinct objects",
  avg_objects_per_node: "Avg objects / node",
  zero_object_nodes: "Zero-object nodes",
  object_merges_summary:
    "Merges within windows — {{merged}} of {{total}} name entries merged into another",
  canonical_name: "Canonical name",
  merged_from: "Merged from",
  window: "Window",
  window_hint:
    "The virtual recording whose names were clustered together. Each window is canonicalized independently; the same name can appear in different groups in different windows.",
  instance_overlap: "Object overlap across cases",
  no_shared_objects: "Cases do not share any object",
  object_shared_by_instances: "{{object}} — shared by {{instances}} different cases",
  nodes: "Nodes",
  avg_children: "Avg children",
  single_child_nodes: "single-child",
  fragmentation: "Fragmentation",
  fragmentation_hint:
    "(1 - duration/span) averaged across nodes and weighted by node duration, so that long nodes count for more than short ones; span is end_timestamp - start_timestamp of the node. If a node's time coverage is a single contiguous block, fragmentation = 0; vice versa, if a node's coverage is very diluted across its span, fragmentation → 1.",
  avg_apps_per_node: "Avg apps / node",
  time_accounting: "Time accounting",
  structured_time: "Structured",
  structured_time_hint:
    "Time share from nodes of this level that have parent nodes. Every L4 node is a root, so all its covered time counts as structured.",
  orphaned_time: "Orphaned",
  orphaned_time_hint:
    "Time share from orphan nodes. Time pruned at L4 reappears as orphan time at L3.",
  orphan_list_button: "{{count}} orphans",
  orphan_list_button_one: "{{count}} orphan",
  orphan_list_title: "Orphaned nodes — L{{depth}} ({{total}})",
  orphan_list_no_goal: "—",
  uncovered_time: "Uncovered",
  uncovered_time_hint: "Recording time no node of this level spans",
  pruned_too_short: "Pruned (too short)",
  pruned_too_short_hint: "L4 cases dropped for falling under the minimum duration {{duration}}",
  pruned_single_child: "Pruned (single child)",
  pruned_single_child_hint: "L4 cases dropped for wrapping a single child (single-step cases)",
  prune_caption:
    "{{count}} cases pruned — {{tooShort}} too short ({{tooShortDetail}}), {{singleChild}} single child ({{singleChildDetail}})",
  prune_caption_one:
    "{{count}} case pruned — {{tooShort}} too short ({{tooShortDetail}}), {{singleChild}} single child ({{singleChildDetail}})",
  distributions: "Distributions",
  metric_duration: "Node duration",
  metric_children: "Children per node",
  vocab_coverage: "Vocabulary coverage",
  invented_names: "Invented names",
  avg_reuse: "Avg re-use",
  avg_reuse_hint:
    "Average number of times an invented name was used again after the node that introduced it. 0 when every invented name was used exactly once.",
  invented: "Invented",
  invented_process_names: "Invented process names",
  invented_step_names: "Invented step names",
  show_invented_names: "Show list",
  name_reuses: "Re-uses",
  name_reuses_hint: "How many times the invented name was used again.",
  annotated_clicks: "Annotated clicks",
  annotated_clicks_per_hour: "Annotated clicks / hour",
  screenshots_used_label: "Screenshots used / total",
  screenshots_used_per_hour: "Screenshots used / hour",
  annotation_funnel_outcome: "Outcome",
  annotation_funnel_clicks: "Clicks",
  annotation_funnel_candidates: "Clicks to annotate",
  annotation_funnel_candidates_hint:
    "Left clicks plus double clicks that survived cleanup — right clicks are never annotated.",
  annotation_funnel_dropped: "Dropped before the model",
  annotation_funnel_sent: "Sent to the vision model",
  annotation_funnel_failed: "Failed (billed, no label)",
  annotation_funnel_annotated: "Annotated",
  drop_reason_concurrent_missing: "concurrent screenshot missing",
  drop_reason_other: "other",
  sent_without_post_window: "no post-click screenshot in the time window",
  sent_post_missing: "post-click screenshot missing on disk",
  concurrent_screenshot_delay: "Concurrent screenshot delay",
  concurrent_screenshot_delay_hint:
    "How far from the click instant its concurrent screenshot (the frame showing what the user clicked on) was captured. Averaged over the clicks that had one.",
  concurrent_screenshot_delay_window_hint:
    "That screenshot is searched in a window of {{window}} around the click, taking the frame closest to the click instant.",
  post_screenshot_delay: "Post-click screenshot delay",
  post_screenshot_delay_hint:
    "How far after the click its post-click screenshot (the frame showing what the click did) was captured. Averaged over the clicks that had one.",
  post_screenshot_delay_window_hint:
    "That screenshot is the earliest frame in a window {{window}} after the click that isn't the concurrent one.",
  stage: "Stage",
  time: "Time",
  time_hint: "Time it takes to execute the analysis stage.",
  cost: "Cost ($)",
  cost_per_hour: "Cost / hour ($)",
  model: "Model",
  token_stats: "Token stats",
  stats_predate_report: "This analysis predates these stats — re-run it to record them.",
  logical_calls: "Logical calls",
  api_calls: "API calls",
  api_calls_hint: "Every request sent to the model, including repair retries.",
  in_per_api_call: "In / API call",
  in_per_api_call_hint:
    "Average input tokens per request sent. The total row shows total input tokens across all requests.",
  out_per_api_call: "Out / API call",
  out_per_api_call_hint:
    "Average output tokens per response received. The total row shows total output tokens across all responses.",
  max_out: "Max out",
  max_out_hint:
    "Largest single response, and how much of the output cap it used. A response truncates when it alone reaches that cap.",
  max_out_cap_hint: "Cap {{cap}} tokens",
  max_out_no_cap_hint: "No output cap on this alias.",
  l2_canonicalization_hint: "Includes the object-name canonicalization pass.",
  tool_rounds: "Tool rounds",
  tool_rounds_hint: "Rounds that called at least one tool, summed over the stage.",
  tool_calls: "Tool calls",
  tool_calls_hint: "Tool calls made by the stage. A round can call multiple tools.",
  tool_use: "Tool use",
  tool_use_hint:
    "Calls made by each stage, and the tool's share of that stage's tool calls. A tool at zero was still sent, and paid for, in every request.",
  tool_name: "Tool",
  tool_log_round: "Round",
  tool_log_ok: "ok",
  tool_log_failed: "failed",
  no_tools_offered: "No stage offered the model any tools.",
  logical_unit_screenshots: "screenshots",
  logical_unit_chunks: "chunks",
  retry_ratio_hint: "{{ratio}} API calls per logical call",
  repair_retries: "Repair retries",
  repair_retries_hint:
    "LLM responses the run could not use, one row per failure: the lifts' failed attempts, which were sent back to the model to fix, and the annotator's vision calls, which fail their click outright instead of retrying.",
  retry_unit: "Chunk / click",
  retry_chunk: "Chunk",
  retry_attempt: "Attempt",
  retry_errors: "Errors",
  no_retries_recorded:
    "No unusable responses — every call parsed and validated first time, and every sent click came back labelled.",
  view_detail: "view",
  vision_failure_click: "Click",
  vision_failure_kind_api_error: "API error",
  vision_failure_kind_truncated: "Truncated output",
  vision_failure_kind_no_json: "No JSON in response",
  vision_failure_kind_malformed_json: "Malformed JSON",
  vision_failure_kind_unknown_target: "Unidentified target",
  total: "Total",
  rerun_total: "This re-run",
  level: "Level",
  business_objects_per_hour: "Business objects / hour",
  warnings_badge: "{{count}} warnings",
  warnings_badge_one: "1 warning",
  warning_in_per_api_call: "the model's output quality may degrade on inputs this long.",
  warning_short_tab_drop:
    "Suspicious number of short tab visits in the recording: {{share}} of the raw events.",
  warning_cleaned_events: "Suspicious low number of raw events survived cleanup.",
  warning_annotated_share: "Low share of clicks annotated.",
  warning_n_vision_truncated:
    "{{count}} vision responses were cut off at the configured output limit and their clicks went unlabelled — raise the model alias's output cap.",
  warning_n_vision_truncated_one:
    "A vision response was cut off at the configured output limit and its click went unlabelled — raise the model alias's output cap.",
  warning_tool_budget_exhausted:
    "{{stage}}: {{count}} logical calls spent all {{max}} tool rounds.",
  warning_tool_budget_exhausted_one:
    "{{stage}}: {{count}} logical call spent all {{max}} tool rounds.",
  warning_annotated_per_hour: "High rate of clicks per hour.",
  warning_screenshots_used: "Low screenshot usage.",
  warning_concurrent_delay:
    "The concurrent screenshot sits {{delay}} from the click on average, above {{limit}}, it may be unreliable to show what was actually clicked.",
  warning_post_delay:
    "The post-click screenshot sits {{delay}} from the click on average, above {{limit}}, may be unreliable to show what the click did.",
  warning_avg_children: "the stage barely groups what is under it.",
  warning_fragmentation: "May be suspiciously high fragmentation.",
  warning_avg_apps: "Suspiciously many apps/node on average.",
  warning_orphaned: "Suspiciously high level of orphaned time.",
  warning_structured_coverage: "Output captures < {{limit}} of the recording",
  warning_pruning: "Pruning discarded a suspiciously high fraction.",
  warning_avg_reuse: "Low re-usage of invented names.",
  warning_clipboard_pairs_per_hour: "An unusually high rate of copy-pasting.",
  warning_instance_overlap:
    "{{count}} objects are referenced by more than one case — the cases may not be as independent as they look.",
  warning_instance_overlap_one:
    "{{count}} object is referenced by more than one case — the cases may not be as independent as they look.",
  warning_business_objects_per_hour: "An unusually high rate of business objects.",
  warning_failed_call: "{{stage}} produced a response the run could not use.",
  virtual_recordings: "Virtual recordings",
  virtual_recordings_hint:
    "Shared time boundaries used by L2 object-name canonicalization and the L3/L4 lifts, followed by the work and truncation recovery recorded by each stage.",
  windows: "Windows",
  windows_hint: "Virtual recordings the timeline was cut into, including ones that held no node.",
  chunks_sent: "Chunks sent",
  chunks_sent_hint:
    "Windows that became a model call: those holding at least one node, or — for the L2 name canonicalization — those with two or more names to cluster.",
  idle_gap_cuts: "Idle-gap cuts",
  idle_gap_cuts_hint:
    "Boundaries placed after a genuine idle stretch long enough to always count as a break (6h).",
  longest_pause_cuts: "Longest-pause cuts",
  longest_pause_cuts_hint:
    "Boundaries placed at the longest usable pause of a section that was over the span limit (20h).",
  max_span_cuts: "Span-limit cuts",
  max_span_cuts_hint:
    "Boundaries forced at the span limit (20h) because the section had no usable pause — these fall mid-work.",
  truncation_splits: "Truncation splits",
  truncation_splits_hint:
    "Chunks whose output overran the model's token limit and were redone as two smaller calls. Each one cost a response the run threw away.",
  split_nodes: "Nodes",
  split_retried_as: "Retried as",
  split_at: "Split at",
  split_basis_idle_gap: "longest idle gap",
  split_basis_median: "median (no idle gap divides the nodes)",
  no_virtual_recordings_recorded:
    "Not recorded — the lift stages of this analysis predate the virtual-recording record.",
  warning_span_limit_cuts:
    "{{stage}} had to force {{count}} cuts at the span limit: no usable pause in over 20h, so those boundaries fall mid-work.",
  warning_span_limit_cuts_one:
    "{{stage}} had to force {{count}} cut at the span limit: no usable pause in over 20h, so that boundary falls mid-work.",
  warning_truncation_split:
    "{{stage}} chunk {{chunk}} overran the output limit and was retried as {{left}} + {{right}} node(s).",
  total_virtual_recordings: "Total virtual recordings",
  shared_partition: "Shared partition",
  shared_partition_hint:
    "Reported split totals match for {{stages}}. Current rules split at internal idle gaps of at least 6h first. Remaining spans over 20h are split recursively at the longest pause leaving at least 2h of wall-clock time on each side, or at 20h if no eligible pause exists.",
  recording_timeline: "Recording timeline",
  timeline_active: "Active",
  timeline_inactive: "Inactive",
  timeline_activity_track: "Active and inactive recording time",
  timeline_axis: "Recording time · local timezone",
  timeline_activity_hint:
    "The upper track shows inferred activity: gaps of at least 2 min are inactive unless the preceding app/window title suggests a meeting or media. Shorter gaps remain active. The lower track shows virtual recordings; dashed markers show their starts after the first. Gaps retain their full duration on the time axis.",
  timeline_no_duration: "No elapsed time is available for this timeline.",
  timeline_not_recorded:
    "The timeline was not saved for this analysis. Rerun from L2 to record its time boundaries and idle intervals.",
  timeline_source:
    "Timeline saved by {{stage}}. Stages without saved timeline data are compared by their split totals only.",
  virtual_recording_label: "Virtual recording",
  virtual_recording_number: "VR {{number}}",
  recording_start: "Recording start",
  idle_gap_cut: "Idle-gap cut",
  longest_pause_cut: "Longest-pause cut",
  span_limit_cut: "Span-limit cut",
  partition_mismatch:
    "These stages recorded different partitions, which can happen after a partial rerun. Each stage’s saved totals and timeline are shown separately below.",
  virtual_stage_details: "Stage-specific work",
  virtual_stage_work_hint:
    "Truncation splits apply only to their stage. Completed chunks include the extra parts created by these splits. A dash means the stage has no saved result.",
  initial_chunks: "Initial chunks",
  completed_chunks: "Completed chunks",
} satisfies AnalysisReportTranslation;
